import { useState, useEffect } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "@reduxjs/toolkit";
import { useParams, useNavigate } from "react-router-dom";
import { productModel } from "../../../models/items/product.model";
import ItemGroupsActions from "../../../store/actions/items/categories/itemGroups/itemGroups.actions";
import productActions from "../../../store/actions/items/item.actions";
import { SkeletonForm } from "../../../components/general/skeleton";
import useDelayedFlag from "../../../hooks/useDelayedFlag";

export const EditProductPageComponent = ({ productActions, categoryActions }) => {
  const { productId } = useParams(); // Get productId from URL
  const navigate = useNavigate();

  // States
  const [productDetails, setProductDetails] = useState(productModel);
  const [categories, setCategories] = useState([]); // List of categories
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const showSkeleton = useDelayedFlag(isLoading);

  // Fetch product details and categories when page loads
  useEffect(() => {
    fetchProductDetails();
    fetchCategories();
  }, [productId]);

  const fetchProductDetails = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await productActions.getProductById(productId);
      const productData = response.payload;

      setProductDetails({
        productName: productData.productName,
        description: productData.description,
        isPreWeighted: productData.isPreWeighted,
        defaultWeight: productData.defaultWeight,
        categoryID: productData.categoryID,
      });
    } catch (error) {
      console.error("Error fetching product details:", error);
      setError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await categoryActions.getCategories({ pageNumber: 1, pageSize: 100 });
      setCategories(response.payload.items);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setProductDetails({
      ...productDetails,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      const updateRequest = {
        productID: productId,
        ...productDetails,
      };

      await productActions.updateProduct(updateRequest);
      alert("Product updated successfully!");
      navigate("/items"); // Navigate back to product list
    } catch (error) {
      console.error("Error updating product:", error);
      alert("Failed to update product. Please try again.");
    }
  };

  const handleCancel = () => {
    navigate("/items");
  };

  return (
    <div>
      <h2>Edit Product</h2>

      {isLoading ? (
        showSkeleton && <SkeletonForm fields={4} />
      ) : error ? (
        <p>Error: {error.message || "Something went wrong"}</p>
      ) : (
        <form onSubmit={handleSubmit}>
          <div>
            <label>Product Name:</label>
            <input
              type="text"
              name="productName"
              value={productDetails.productName}
              onChange={handleInputChange}
              required
            />
          </div>

          <div>
            <label>Description:</label>
            <textarea
              name="description"
              value={productDetails.description}
              onChange={handleInputChange}
              required
            />
          </div>

          <div>
            <label>Category:</label>
            <select
              name="categoryID"
              value={productDetails.categoryID}
              onChange={handleInputChange}
              required
            >
              <option value="">-- Select Category --</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.categoryName}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label>
              <input
                type="checkbox"
                name="isPreWeighted"
                checked={productDetails.isPreWeighted}
                onChange={handleInputChange}
              />
              Pre-Weighted
            </label>
          </div>

          {productDetails.isPreWeighted && (
            <div>
              <label>Default Weight (kg):</label>
              <input
                type="number"
                name="defaultWeight"
                value={productDetails.defaultWeight || ""}
                onChange={handleInputChange}
                min="0"
                step="0.01"
              />
            </div>
          )}

          <div>
            <button type="submit">Save Changes</button>
            <button type="button" onClick={handleCancel}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

const mapDispatchToProps = (dispatch) => {
  return {
    productActions: bindActionCreators(productActions, dispatch),
    categoryActions: bindActionCreators(ItemGroupsActions, dispatch),
  };
};

export default connect(null, mapDispatchToProps)(EditProductPageComponent);
